import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MapView } from '@/components/MapView';
import { BusinessSheet } from '@/components/BusinessSheet';
import { AddBusinessDialog } from '@/components/AddBusinessDialog';
import { SuggestBusinessDialog } from '@/components/SuggestBusinessDialog';
import { DemographicsDialog } from '@/components/DemographicsDialog';
import { NeighborhoodFilter } from '@/components/NeighborhoodFilter';
import { 
  MapPin, 
  Plus, 
  LogIn, 
  LogOut, 
  User,
  Menu,
  X,
  BadgeCheck,
  Search,
  Lightbulb,
  Settings
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "All Categories",
  "Restaurant",
  "Cafe",
  "Bar",
  "Retail",
  "Salon/Barbershop",
  "Grocery",
  "Convenience Store",
  "Pharmacy",
  "Gym",
  "Arts Venue",
  "Public Services",
  "Bank",
  "Other"
];

export default function HomePage() {
  const { user, logout, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [businesses, setBusinesses] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [neighborhoodCoords, setNeighborhoodCoords] = useState({});
  const [selectedNeighborhood, setSelectedNeighborhood] = useState(null);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showAddBusiness, setShowAddBusiness] = useState(false);
  const [showSuggestBusiness, setShowSuggestBusiness] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [showDemographics, setShowDemographics] = useState(false);

  // Check if user needs to complete demographics
  useEffect(() => {
    if (user && !user.demographics_completed) {
      setShowDemographics(true);
    }
  }, [user]);

  useEffect(() => {
    fetchInitialData();
  }, []);
  
  // Handle shared business link (via URL parameter)
  useEffect(() => {
    const businessId = searchParams.get('business');
    if (businessId && businesses.length > 0) {
      const business = businesses.find(b => b.id === businessId);
      if (business) {
        setSelectedBusiness(business);
        // Clear the URL parameter after opening
        setSearchParams({});
      }
    }
  }, [businesses, searchParams, setSearchParams]);

  const fetchInitialData = async () => {
    try {
      const [bizRes, neighborhoodRes] = await Promise.all([
        axios.get(`${API}/businesses`),
        axios.get(`${API}/neighborhoods`)
      ]);
      setBusinesses(bizRes.data);
      setNeighborhoods(neighborhoodRes.data.neighborhoods);
      setNeighborhoodCoords(neighborhoodRes.data.coordinates);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinesses = useCallback(async () => {
    try {
      let url = `${API}/businesses`;
      const params = new URLSearchParams();
      if (selectedNeighborhood) params.append('neighborhood', selectedNeighborhood);
      if (selectedCategory && selectedCategory !== 'All Categories') params.append('category', selectedCategory);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setBusinesses(response.data);
    } catch (error) {
      console.error('Error fetching businesses:', error);
    }
  }, [selectedNeighborhood, selectedCategory]);

  useEffect(() => {
    fetchBusinesses();
  }, [fetchBusinesses]);

  // Debounced search
  const handleSearch = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults(null);
      return;
    }
    try {
      let url = `${API}/businesses?search=${encodeURIComponent(query)}`;
      if (selectedCategory && selectedCategory !== 'All Categories') {
        url += `&category=${encodeURIComponent(selectedCategory)}`;
      }
      if (selectedNeighborhood) {
        url += `&neighborhood=${encodeURIComponent(selectedNeighborhood)}`;
      }
      const response = await axios.get(url);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Search error:', error);
    }
  }, [selectedCategory, selectedNeighborhood]);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  const handleBusinessAdded = (newBusiness) => {
    setBusinesses(prev => [...prev, newBusiness]);
    setShowAddBusiness(false);
  };

  const handleReviewAdded = () => {
    fetchBusinesses();
    if (selectedBusiness) {
      // Refresh the selected business data
      axios.get(`${API}/businesses/${selectedBusiness.id}`)
        .then(res => setSelectedBusiness(res.data))
        .catch(console.error);
    }
  };

  return (
    <div className="h-screen flex flex-col" data-testid="home-page">
      {/* Header */}
      <header className="h-16 bg-card border-b flex items-center justify-between px-4 lg:px-6 relative" style={{ zIndex: 9999 }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
            <MapPin className="w-5 h-5 text-primary-foreground" />
          </div>
          <h1 className="text-xl font-display font-bold text-primary hidden sm:block">Greenish</h1>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-3">
          {user && (
            <Button 
              onClick={() => setShowAddBusiness(true)}
              className="rounded-full bg-primary hover:bg-primary/90"
              data-testid="add-business-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Business
            </Button>
          )}
          
          {authLoading ? null : user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-full gap-2" data-testid="user-menu-btn">
                  <User className="w-4 h-4" />
                  <span className="hidden lg:inline">{user.name}</span>
                  {user.is_verified_local && (
                    <BadgeCheck className="w-4 h-4 text-primary" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user.name}</p>
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                  {user.is_verified_local ? (
                    <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-primary/10 text-primary">
                      <BadgeCheck className="w-3 h-3" />
                      Verified Local
                    </span>
                  ) : (
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {5 - (user.review_count || 0)} more reviews to become Verified Local
                    </p>
                  )}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/admin" className="cursor-pointer" data-testid="admin-link">
                    <Settings className="w-4 h-4 mr-2" />
                    Admin Panel
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login">
              <Button variant="outline" className="rounded-full gap-2" data-testid="login-btn">
                <LogIn className="w-4 h-4" />
                Sign In
              </Button>
            </Link>
          )}
        </div>

        {/* Mobile Menu Button */}
        <Button 
          variant="ghost" 
          size="icon" 
          className="md:hidden"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed top-16 left-0 right-0 bg-card border-b p-4 space-y-3 animate-slide-up shadow-lg" style={{ zIndex: 9998 }}>
          {user ? (
            <>
              <div className="text-sm text-muted-foreground px-2">
                Signed in as {user.name}
              </div>
              <Button 
                onClick={() => {
                  setShowAddBusiness(true);
                  setMobileMenuOpen(false);
                }}
                className="w-full rounded-full bg-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Business
              </Button>
              <Link to="/admin" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="outline" className="w-full rounded-full mt-2">
                  <Settings className="w-4 h-4 mr-2" />
                  Admin Panel
                </Button>
              </Link>
              <Button 
                variant="outline" 
                className="w-full rounded-full"
                onClick={() => {
                  logout();
                  setMobileMenuOpen(false);
                }}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </>
          ) : (
            <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="outline" className="w-full rounded-full">
                <LogIn className="w-4 h-4 mr-2" />
                Sign In
              </Button>
            </Link>
          )}
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 relative overflow-hidden" style={{ height: 'calc(100vh - 64px)' }}>
        {/* Map */}
        <MapView 
          businesses={searchResults || businesses}
          neighborhoodCoords={neighborhoodCoords}
          selectedNeighborhood={searchResults ? null : selectedNeighborhood}
          onBusinessClick={setSelectedBusiness}
          loading={loading}
        />

        {/* Floating Neighborhood Filter */}
        <div className="absolute top-4 left-4 right-4 md:left-4 md:right-auto md:max-w-2xl z-[1000]">
          <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border overflow-hidden">
            {/* Search Bar and Category Filter */}
            <div className="p-3 border-b">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search businesses..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-10 bg-muted/50 border-0 focus-visible:ring-1"
                    data-testid="search-input"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-[140px] sm:w-[160px] h-10 bg-muted/50 border-0 shrink-0" data-testid="category-filter">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {(searchResults !== null || (selectedCategory && selectedCategory !== 'All Categories')) && (
                <p className="text-xs text-muted-foreground mt-2">
                  Found {(searchResults || businesses).length} {(searchResults || businesses).length === 1 ? 'business' : 'businesses'}
                  {searchQuery && ` matching "${searchQuery}"`}
                  {selectedCategory && selectedCategory !== 'All Categories' && ` in ${selectedCategory}`}
                </p>
              )}
            </div>
            {/* Neighborhood Filter */}
            <NeighborhoodFilter
              neighborhoods={neighborhoods}
              selected={selectedNeighborhood}
              onSelect={(hood) => {
                setSelectedNeighborhood(hood);
                setSearchQuery('');
                setSearchResults(null);
              }}
            />
          </div>
        </div>

        {/* Legend */}
        <div className="absolute bottom-6 left-4 z-[1000] bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg border">
          <div className="text-xs font-semibold text-foreground mb-2">Rating Guide</div>
          <div className="flex flex-col gap-2 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#10B981' }}></div>
              <span>4-5 Stars - Great</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#F59E0B' }}></div>
              <span>3 Stars - Mixed</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#EF4444' }}></div>
              <span>1-2 Stars - Flagged</span>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full mt-3 text-xs rounded-full"
            onClick={() => setShowSuggestBusiness(true)}
            data-testid="suggest-business-btn"
          >
            <Lightbulb className="w-3 h-3 mr-1.5" />
            Suggest a Business
          </Button>
        </div>
      </main>

      {/* Business Detail Sheet */}
      <BusinessSheet
        business={selectedBusiness}
        onClose={() => setSelectedBusiness(null)}
        onReviewAdded={handleReviewAdded}
      />

      {/* Add Business Dialog */}
      <AddBusinessDialog
        open={showAddBusiness}
        onOpenChange={setShowAddBusiness}
        neighborhoods={neighborhoods}
        neighborhoodCoords={neighborhoodCoords}
        onSuccess={handleBusinessAdded}
      />

      {/* Suggest Business Dialog (for non-logged-in users) */}
      <SuggestBusinessDialog
        open={showSuggestBusiness}
        onOpenChange={setShowSuggestBusiness}
        neighborhoods={neighborhoods}
      />

      {/* Demographics Dialog (for new users) */}
      <DemographicsDialog
        open={showDemographics}
        onComplete={() => setShowDemographics(false)}
      />
    </div>
  );
}
